
import React, { useState } from 'react';
import { Category, User, UserProgress } from '../types';
import { Trophy, ChevronRight, Swords, Heart } from 'lucide-react';
// Fix: Import SupportModal from Layouts instead of the decommissioned file
import { SupportModal, ParentalGate } from './Layouts';
import { COBALT_BLUE } from '../constants';

interface CategoryCatalogueProps {
  categories: Category[];
  onSelect: (cat: Category) => void;
  user: User | null;
  progress: UserProgress;
  onToggleSponsorship: (status: boolean) => void;
  onAcceptDuel: () => void;
  triggerOnlineAction?: (featureName: string, action: () => void) => void;
}

const CategoryCatalogue: React.FC<CategoryCatalogueProps> = ({ 
  categories, 
  onSelect, 
  user, 
  progress, 
  onToggleSponsorship,
  onAcceptDuel,
  triggerOnlineAction
}) => {
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isParentGateOpen, setIsParentGateOpen] = useState(false);
  const solvedCount = progress.solvedPuzzles.length;

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-white pt-safe pb-safe px-safe">
      <div className="max-w-5xl mx-auto w-full">
        <header className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <h2 className="text-xs uppercase tracking-[0.3em] font-bold mb-1" style={{ color: COBALT_BLUE }}>Catalogue</h2>
            <h1 className="text-4xl md:text-5xl font-cinzel font-bold text-gray-900">Choose a Path</h1>
            <p className="text-gray-500 mt-2 font-medium">Shalom, {user?.username || 'Beloved'}!</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
              <div className="p-2.5 rounded-full bg-blue-50">
                <Trophy className="w-5 h-5" style={{ color: COBALT_BLUE }} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Mastered</p>
                <p className="text-xl font-bold text-gray-800 leading-none">{solvedCount}</p>
              </div>
            </div>
            <button
              onClick={() => setIsParentGateOpen(true)}
              className="p-3 bg-rose-50 text-rose-500 hover:bg-rose-100 rounded-2xl border border-rose-100/50 transition-colors active:scale-95 shadow-sm cursor-pointer flex items-center justify-center"
              title="Unlock & Support Options"
            >
              <Heart className="w-5 h-5 fill-rose-500 animate-pulse" />
            </button>
          </div>
        </header>

        {/* Accept a Challenge section */}
        <div className="mb-8 p-6 rounded-[2rem] bg-slate-50/70 border border-slate-100/80 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-left w-full">
          <div className="space-y-1 text-left">
            <h2 className="text-2xl font-cinzel font-bold text-gray-900 text-left">Accept a Challenge</h2>
            <p className="text-xs text-slate-500 font-semibold max-w-xl leading-relaxed text-left">
              Solve a customised puzzle from another player and beat their score
            </p>
          </div>
          <button
            onClick={() => {
              if (triggerOnlineAction) {
                triggerOnlineAction('Faith Duels / Custom Challenges', onAcceptDuel);
              } else {
                onAcceptDuel();
              }
            }}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3.5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 cursor-pointer flex items-center gap-2 text-xs"
          >
            <Swords className="w-4 h-4 flex-shrink-0" />
            Post Link
          </button>
        </div>

        {/* Categories Section Heading */}
        <div className="mb-6 p-6 rounded-[2rem] bg-slate-50/70 border border-slate-100/80 text-left w-full">
          <div className="space-y-1 text-left">
            <h2 className="text-2xl font-cinzel font-bold text-gray-900 text-left">Pick a Puzzle to Solve</h2>
            <p className="text-xs text-slate-500 font-semibold max-w-xl leading-relaxed text-left">
              Browse and select a puzzle from the categories below
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
          {categories.map((cat) => (
            <button 
              key={cat.id}
              onClick={() => onSelect(cat)}
              className="group relative bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all text-left flex items-start gap-5 overflow-hidden active:scale-95"
            >
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-500 bg-blue-50/50">
                <img 
                  src={cat.iconUrl} 
                  alt={cat.name} 
                  className="w-10 h-10 object-contain"
                  onError={(e) => {
                     (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(cat.name)}&background=random`;
                  }}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-blue-700 transition-colors">{cat.name}</h3>
                <p className="text-sm text-gray-400 font-medium">{cat.puzzles.length} Puzzles</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all mt-1" />
            </button>
          ))}
        </div>
      </div>

      <ParentalGate 
        isOpen={isParentGateOpen}
        onClose={() => setIsParentGateOpen(false)}
        onSuccess={() => {
          setIsParentGateOpen(false);
          setIsSupportModalOpen(true);
        }}
      />

      <SupportModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)} 
        onUnLockAll={() => onToggleSponsorship(true)}
      />
    </div>
  );
};

export default CategoryCatalogue;
